import type { CountyRecordHub } from "@/data/countyRecordHubs";
import type { PublicRecordSource } from "@/data/publicRecordsDirectory";

/** URL-persisted geometry availability filter. */
export type GeometryFilter = "all" | "available" | "missing";

export const GEOMETRY_OPTIONS: Array<{ value: GeometryFilter; label: string }> = [
  { value: "all", label: "Any geometry" },
  { value: "available", label: "Geometry available" },
  { value: "missing", label: "Geometry missing" },
];

export function isGeometryFilter(value: unknown): value is GeometryFilter {
  return value === "all" || value === "available" || value === "missing";
}

/** A county hub has geometry when it publishes at least one spatial REST layer. */
export function hubHasGeometry(hub: CountyRecordHub): boolean {
  return (
    hub.availability.includes("arcgis") &&
    hub.restLayers.some((layer) => layer.geometryType.startsWith("esriGeometry"))
  );
}

/** A statewide/federal source has geometry when it exposes GIS or REST access. */
export function recordHasGeometry(record: PublicRecordSource): boolean {
  return record.accessType === "rest-arcgis" || record.accessType === "free-gis";
}

export function matchesGeometryFilter(hasGeometry: boolean, filter: GeometryFilter): boolean {
  if (filter === "available") return hasGeometry;
  if (filter === "missing") return !hasGeometry;
  return true;
}

/** Result counts for each geometry mode, used by the counts display. */
export interface GeometryCounts {
  all: number;
  available: number;
  missing: number;
}

export function tallyGeometry(items: boolean[]): GeometryCounts {
  const available = items.filter(Boolean).length;
  return { all: items.length, available, missing: items.length - available };
}

export function addGeometryCounts(a: GeometryCounts, b: GeometryCounts): GeometryCounts {
  return {
    all: a.all + b.all,
    available: a.available + b.available,
    missing: a.missing + b.missing,
  };
}
